import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, Phone, Mail, MapPin, Users, ChefHat, Sparkles, ArrowRight, Check, Star, Camera } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_epicure-convivial/artifacts/r9kyllft_IMG-20250323-WA0015.jpg";

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Accueil", path: "/" },
    { name: "Notre Carte", path: "/menu" },
    { name: "Cocktails", path: "/cocktails" },
    { name: "Galerie", path: "/galerie" },
    { name: "À Propos", path: "/a-propos" },
    { name: "Commander", path: "/commander" },
  ];

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-emerald-deep/90 backdrop-blur-xl border-b border-gold/20"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="font-script text-4xl md:text-5xl text-gold hover:text-gold-light transition-colors" data-testid="logo-link">
              Pikapero
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`font-secondary text-xs uppercase tracking-widest transition-colors ${
                    location.pathname === link.path
                      ? "text-gold"
                      : "text-cream/80 hover:text-gold"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden text-gold p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        data-testid="mobile-menu"
        className={`fixed inset-0 z-40 bg-emerald-deep/98 backdrop-blur-xl transition-all duration-500 lg:hidden ${
          isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <div className="flex flex-col items-center justify-center h-full gap-6">
          <h2 className="font-script text-5xl text-gold mb-8">Pikapero</h2>
          {navLinks.map((link, idx) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
              className="font-primary text-2xl text-cream hover:text-gold transition-colors"
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-emerald-deep border-t border-gold/20 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
          {/* Brand */}
          <div className="text-center md:text-left">
            <h3 className="font-script text-5xl md:text-6xl text-gold mb-4">Pikapero</h3>
            <p className="font-secondary text-gold/70 text-sm italic mb-2">Plus ça pik plus c'est bon !</p>
            <p className="font-secondary text-cream/70 text-sm leading-relaxed">
              Plateaux apéritifs gourmands<br />
              aux saveurs d'Afrique, des Îles et d'Orient
            </p>
          </div>

          {/* Contact */}
          <div className="text-center">
            <h4 className="font-primary text-xl text-cream mb-4">Contact</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-3 text-cream/70">
                <MapPin size={18} className="text-gold" />
                <span className="font-secondary text-sm">Bordeaux et alentours</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-cream/70">
                <Phone size={18} className="text-gold" />
                <span className="font-secondary text-sm">06 49 30 13 75</span>
              </div>
              <div className="flex items-center justify-center gap-3 text-cream/70">
                <Mail size={18} className="text-gold" />
                <span className="font-secondary text-sm">inivacristal30@gmail.com</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="text-center md:text-right">
            <h4 className="font-primary text-xl text-cream mb-4">Services</h4>
            <p className="font-secondary text-cream/70 text-sm mb-2">
              Livraison • Dressage • Assistance traiteur
            </p>
            <p className="font-secondary text-cream/70 text-sm">
              Sur réservation uniquement<br />
              Commandes 48h à l'avance
            </p>
            <Link
              to="/commander"
              data-testid="footer-cta-btn"
              className="inline-block mt-6 border border-gold text-gold px-6 py-2 font-secondary text-sm uppercase tracking-widest hover:bg-gold hover:text-black transition-all duration-300"
            >
              Commander
            </Link>
          </div>
        </div>

        <div className="border-t border-gold/10 mt-12 pt-8 text-center">
          <p className="font-secondary text-cream/40 text-xs">
            © 2024 Pikapero - Plus ça pik plus c'est bon. Bordeaux, France.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/4253319/pexels-photo-4253319.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-deep via-emerald-deep/80 to-emerald-deep/40" />
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="font-secondary text-gold text-sm uppercase tracking-[0.3em] mb-6 animate-fade-in">
            Traiteur Apéritif Bordeaux
          </p>
          <h1 className="font-script text-6xl md:text-8xl lg:text-9xl text-gold mb-4 animate-fade-in" style={{ animationDelay: '100ms' }}>
            Pikapero
          </h1>
          <p className="font-secondary text-gold/80 text-sm md:text-base italic mb-6 animate-fade-in" style={{ animationDelay: '150ms' }}>
            Plus ça pik plus c'est bon !
          </p>
          <p className="font-primary text-2xl md:text-3xl text-cream mb-4 animate-fade-in" style={{ animationDelay: '200ms' }}>
            Plateaux Apéritifs Gourmands
          </p>
          <p className="font-secondary text-cream/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 animate-fade-in" style={{ animationDelay: '300ms' }}>
            Saveurs d'Afrique, des Îles et d'Orient<br />
            pour vos moments de partage
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <Link
              to="/commander"
              data-testid="hero-cta-btn"
              className="border border-gold bg-gold text-black px-10 py-4 font-secondary text-sm uppercase tracking-widest hover:bg-gold-light transition-all duration-300"
            >
              Demander un Devis
            </Link>
            <Link
              to="/menu"
              data-testid="hero-menu-btn"
              className="border border-gold text-gold px-10 py-4 font-secondary text-sm uppercase tracking-widest hover:bg-gold hover:text-black transition-all duration-300"
            >
              Découvrir notre Carte
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-gold rounded-full" />
          </div>
        </div>
      </section>

      {/* Offers Section */}
      <section data-testid="offers-section" className="bg-black py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Nos Formules</p>
            <h2 className="font-primary text-4xl md:text-5xl text-cream">Offres Apéritif</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                image: "https://images.unsplash.com/photo-1541014741259-de529411b96a?w=600",
                title: "Buffet Mignardises",
                price: "18€/pers",
                desc: "Assortiment de mignardises salées et sucrées"
              },
              {
                image: "https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=600",
                title: "Buffet + Cocktail",
                price: "23€/pers",
                desc: "Mignardises + Cocktail au choix"
              },
              {
                image: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600",
                title: "Formule Personnalisée",
                price: "Sur devis",
                desc: "Composition à la carte selon vos envies"
              }
            ].map((offer, idx) => (
              <div
                key={idx}
                data-testid={`offer-card-${idx}`}
                className="group overflow-hidden border border-gold/30 hover:border-gold/60 transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={offer.image} 
                    alt={offer.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 text-center bg-black">
                  <h3 className="font-primary text-xl text-cream mb-2">{offer.title}</h3>
                  <p className="font-script text-3xl text-gold mb-3">{offer.price}</p>
                  <p className="font-secondary text-cream/70 text-sm">{offer.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu Preview */}
      <section data-testid="menu-preview" className="bg-neutral-950 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Notre Carte</p>
            <h2 className="font-primary text-4xl md:text-5xl text-cream">Au Fil des Recettes</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600", title: "Mini Salés", count: "26+ variétés", link: "/menu" },
              { image: "https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=600", title: "Mini Sucrés", count: "20+ douceurs", link: "/menu" },
              { image: "https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?w=600", title: "Classiques", count: "6 sandwiches", link: "/menu" },
              { image: "https://images.unsplash.com/photo-1536935338788-846bb9981813?w=600", title: "Cocktails", count: "16 recettes", link: "/cocktails" }
            ].map((cat, idx) => (
              <Link
                key={idx}
                to={cat.link}
                data-testid={`category-card-${idx}`}
                className="group overflow-hidden border border-white/10 hover:border-gold/50 transition-all duration-300"
              >
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={cat.image} 
                    alt={cat.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="p-4 text-center bg-black">
                  <h3 className="font-primary text-xl text-cream mb-1">{cat.title}</h3>
                  <p className="font-secondary text-gold text-sm">{cat.count}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/menu"
              data-testid="view-full-menu-btn"
              className="inline-flex items-center gap-2 text-gold font-secondary text-sm uppercase tracking-widest hover:gap-4 transition-all"
            >
              Voir la carte complète <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section data-testid="features-section" className="bg-black py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Nos Services</p>
            <h2 className="font-primary text-4xl md:text-5xl text-cream">À Votre Service</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                icon: <ChefHat size={32} />,
                title: "Livraison",
                desc: "Livraison à domicile ou sur le lieu de votre événement"
              },
              {
                icon: <Sparkles size={32} />,
                title: "Dressage",
                desc: "Mise en place et présentation soignée de vos plateaux"
              },
              {
                icon: <Users size={32} />,
                title: "Assistance Traiteur",
                desc: "Service complet pour vos réceptions et événements"
              }
            ].map((feature, idx) => (
              <div
                key={idx}
                data-testid={`feature-card-${idx}`}
                className="text-center border-l border-gold/30 pl-8 py-4"
              >
                <div className="text-gold mb-4">{feature.icon}</div>
                <h3 className="font-primary text-xl text-cream mb-3">{feature.title}</h3>
                <p className="font-secondary text-cream/70 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="bg-emerald-deep py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-script text-4xl md:text-6xl text-gold mb-6">Un événement à préparer ?</h2>
          <p className="font-secondary text-cream/80 text-lg mb-10 max-w-2xl mx-auto">
            Anniversaire, mariage, soirée entre amis ou événement d'entreprise... 
            Nous créons le plateau parfait pour votre occasion.
          </p>
          <Link
            to="/commander"
            data-testid="cta-quote-btn"
            className="inline-block border border-gold bg-gold text-black px-10 py-4 font-secondary text-sm uppercase tracking-widest hover:bg-gold-light transition-all duration-300"
          >
            Demander un Devis Gratuit
          </Link>
        </div>
      </section>
    </div>
  );
};

// Menu Page
const MenuPage = () => {
  const [menuData, setMenuData] = useState(null);
  const [activeTab, setActiveTab] = useState('mini_sales');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API}/menu/all`);
        setMenuData(response.data);
      } catch (e) {
        console.error("Error fetching menu:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const tabs = [
    { id: 'mini_sales', name: 'Mini Salés' },
    { id: 'mini_sucres', name: 'Mini Sucrés' },
    { id: 'classiques', name: 'Classiques' }
  ];

  return (
    <div data-testid="menu-page" className="min-h-screen bg-black pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 text-center">
        <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Notre Carte</p>
        <h1 className="font-primary text-4xl md:text-6xl text-cream mb-6">Au Fil des Recettes</h1>
        <p className="font-secondary text-cream/70 max-w-2xl mx-auto px-6">
          Des créations gourmandes inspirées des saveurs du monde
        </p>
      </section>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <div data-testid="menu-tabs" className="flex flex-wrap justify-center gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-secondary text-sm uppercase tracking-widest border transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gold text-black border-gold'
                  : 'border-gold/50 text-gold hover:bg-gold hover:text-black'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>
      </div>

      {/* Menu Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : menuData && (
          <div data-testid="menu-content">
            {activeTab === 'mini_sales' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {menuData.mini_sales.map((item, idx) => (
                  <div key={idx} data-testid={`menu-item-${idx}`} className="p-4 border border-white/10 hover:border-gold/50 transition-colors bg-white/5">
                    <p className="font-secondary text-cream text-sm">{item}</p>
                  </div>
                ))}
              </div>
            )}
            
            {activeTab === 'mini_sucres' && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {menuData.mini_sucres.map((item, idx) => (
                  <div key={idx} data-testid={`menu-item-${idx}`} className="p-4 border border-white/10 hover:border-gold/50 transition-colors bg-white/5">
                    <p className="font-secondary text-cream text-sm">{item}</p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'classiques' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {menuData.classiques.map((item, idx) => (
                  <div key={idx} data-testid={`menu-item-${idx}`} className="p-6 border border-white/10 hover:border-gold/50 transition-colors bg-white/5">
                    <h3 className="font-primary text-xl text-cream mb-2">{item.name}</h3>
                    <p className="font-secondary text-cream/70 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Offers */}
        <div className="mt-16 p-8 border border-gold/30 bg-gold/5">
          <h3 className="font-primary text-2xl text-cream text-center mb-8">Nos Formules</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="font-primary text-lg text-cream">Buffet Mignardises</p>
              <p className="font-script text-3xl text-gold my-2">18€/pers</p>
              <p className="font-secondary text-cream/70 text-sm">Assortiment salé & sucré</p>
            </div>
            <div className="text-center">
              <p className="font-primary text-lg text-cream">Buffet + Cocktail</p>
              <p className="font-script text-3xl text-gold my-2">23€/pers</p>
              <p className="font-secondary text-cream/70 text-sm">Mignardises + Cocktail</p>
            </div>
            <div className="text-center">
              <p className="font-primary text-lg text-cream">Formule Personnalisée</p>
              <p className="font-script text-3xl text-gold my-2">Sur devis</p>
              <p className="font-secondary text-cream/70 text-sm">Selon vos envies</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Link
            to="/commander"
            data-testid="menu-cta-btn"
            className="inline-block border border-gold bg-gold text-black px-10 py-4 font-secondary text-sm uppercase tracking-widest hover:bg-gold-light transition-all duration-300"
          >
            Demander un Devis
          </Link>
        </div>
      </section>
    </div>
  );
};

// Cocktails Page
const CocktailsPage = () => {
  const [cocktails, setCocktails] = useState(null);
  const [activeCategory, setActiveCategory] = useState('mojitos');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCocktails = async () => {
      try {
        const response = await axios.get(`${API}/menu/cocktails`);
        setCocktails(response.data.items);
      } catch (e) {
        console.error("Error fetching cocktails:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchCocktails();
  }, []);

  const categories = [
    { id: 'mojitos', name: 'Mojitos' },
    { id: 'pina_colada', name: 'Piña Colada' },
    { id: 'sex_beach', name: 'Sex on the Beach' },
    { id: 'punch', name: 'Punch Tropicaux' }
  ];

  return (
    <div data-testid="cocktails-page" className="min-h-screen bg-black pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 text-center">
        <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Nos Créations</p>
        <h1 className="font-primary text-4xl md:text-6xl text-cream mb-6">Carte des Cocktails</h1>
        <p className="font-secondary text-cream/70 max-w-2xl mx-auto px-6">
          Des cocktails frais et savoureux pour accompagner vos apéritifs
        </p>
      </section>

      {/* Category Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <div data-testid="cocktail-tabs" className="flex flex-wrap justify-center gap-4">
          {categories.map((cat) => (
            <button
              key={cat.id}
              data-testid={`cocktail-tab-${cat.id}`}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-6 py-3 font-secondary text-sm uppercase tracking-widest border transition-all duration-300 ${
                activeCategory === cat.id
                  ? 'bg-gold text-black border-gold'
                  : 'border-gold/50 text-gold hover:bg-gold hover:text-black'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Cocktails Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : cocktails && (
          <div data-testid="cocktails-grid" className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {cocktails[activeCategory]?.map((cocktail, idx) => (
              <div
                key={idx}
                data-testid={`cocktail-card-${idx}`}
                className="p-6 border border-white/10 hover:border-gold/50 transition-colors bg-white/5"
              >
                <h3 className="font-primary text-xl text-gold mb-4">{cocktail.name}</h3>
                <ul className="space-y-2">
                  {cocktail.ingredients.map((ing, i) => (
                    <li key={i} className="font-secondary text-cream/70 text-sm flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-gold rounded-full" />
                      {ing}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="font-secondary text-cream/70 mb-6">
            Envie d'un cocktail personnalisé pour votre événement ?
          </p>
          <Link
            to="/commander"
            data-testid="cocktails-cta-btn"
            className="inline-block border border-gold bg-gold text-black px-10 py-4 font-secondary text-sm uppercase tracking-widest hover:bg-gold-light transition-all duration-300"
          >
            Demander un Devis
          </Link>
        </div>
      </section>
    </div>
  );
};

// Gallery Page
const GalleryPage = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [activeTheme, setActiveTheme] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGalleryItems(response.data.items);
      } catch (e) {
        console.error("Error fetching gallery:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const themes = [
    { id: 'all', name: 'Tous' },
    { id: 'afrique', name: 'Afrique' },
    { id: 'iles', name: 'Îles' },
    { id: 'orient', name: 'Orient' },
    { id: 'sucre', name: 'Sucré' },
    { id: 'brunch', name: 'Brunch' }
  ];

  const filteredItems = activeTheme === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.theme === activeTheme);

  const themeColors = {
    afrique: 'from-amber-900/80',
    iles: 'from-cyan-900/80',
    orient: 'from-rose-900/80',
    sucre: 'from-pink-900/80',
    brunch: 'from-orange-900/80'
  };

  return (
    <div data-testid="gallery-page" className="min-h-screen bg-emerald-deep pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 text-center">
        <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Nos Créations</p>
        <h1 className="font-primary text-4xl md:text-6xl text-cream mb-6">Galerie Photos</h1>
        <p className="font-secondary text-cream/70 max-w-2xl mx-auto px-6">
          Découvrez nos plateaux classés par thème. Photos à venir très prochainement !
        </p>
      </section>

      {/* Theme Tabs */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <div data-testid="gallery-tabs" className="flex flex-wrap justify-center gap-3">
          {themes.map((theme) => (
            <button
              key={theme.id}
              data-testid={`gallery-tab-${theme.id}`}
              onClick={() => setActiveTheme(theme.id)}
              className={`px-5 py-2 font-secondary text-xs uppercase tracking-widest border transition-all duration-300 ${
                activeTheme === theme.id
                  ? 'bg-gold text-black border-gold'
                  : 'border-gold/50 text-gold hover:bg-gold hover:text-black'
              }`}
            >
              {theme.name}
            </button>
          ))}
        </div>
      </div>

      {/* Gallery Grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div data-testid="gallery-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, idx) => (
              <div
                key={item.id}
                data-testid={`gallery-item-${item.id}`}
                className="group relative overflow-hidden border border-white/10 hover:border-gold/50 transition-all duration-300"
              >
                {item.placeholder ? (
                  // Placeholder design
                  <div className={`aspect-[4/3] bg-gradient-to-br ${themeColors[item.theme] || 'from-emerald-900/80'} to-black/90 flex flex-col items-center justify-center p-6`}>
                    <div className="w-16 h-16 border-2 border-gold/30 rounded-full flex items-center justify-center mb-4">
                      <Camera size={32} className="text-gold/50" />
                    </div>
                    <p className="font-secondary text-gold/70 text-sm uppercase tracking-widest">Photo à venir</p>
                  </div>
                ) : (
                  // Actual image
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                )}
                
                {/* Info overlay */}
                <div className="p-4 bg-black/50">
                  <span className="font-secondary text-gold text-xs uppercase tracking-widest">
                    {themes.find(t => t.id === item.theme)?.name || item.theme}
                  </span>
                  <h3 className="font-primary text-lg text-cream mt-1">{item.title}</h3>
                  <p className="font-secondary text-cream/70 text-sm mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Info message */}
        <div className="mt-16 p-8 border border-gold/30 bg-gold/5 text-center">
          <h3 className="font-primary text-2xl text-cream mb-4">Galerie en construction</h3>
          <p className="font-secondary text-cream/70 mb-6">
            Nos photos de plateaux arrivent très bientôt ! En attendant, n'hésitez pas à nous contacter pour découvrir nos créations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-cream/80">
              <Phone size={18} className="text-gold" />
              <span className="font-secondary text-sm">06 49 30 13 75</span>
            </div>
            <div className="flex items-center gap-2 text-cream/80">
              <Mail size={18} className="text-gold" />
              <span className="font-secondary text-sm">inivacristal30@gmail.com</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page
const AboutPage = () => {
  return (
    <div data-testid="about-page" className="min-h-screen bg-emerald-deep pt-24">
      {/* Hero */}
      <section className="py-16 md:py-24 text-center">
        <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Notre Histoire</p>
        <h1 className="font-primary text-4xl md:text-6xl text-cream mb-6">À Propos de Pikapero</h1>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <h2 className="font-script text-5xl text-gold mb-4">Pikapero</h2>
            <p className="font-secondary text-gold/80 italic mb-6">Plus ça pik plus c'est bon !</p>
            <p className="font-secondary text-cream/80 leading-relaxed mb-6">
              Pikapero est née d'une passion : celle de partager les saveurs qui ont bercé notre enfance 
              et nos voyages. De l'Afrique aux Antilles, en passant par l'Orient, chaque bouchée est 
              une invitation à l'évasion.
            </p>
            <p className="font-secondary text-cream/80 leading-relaxed mb-6">
              Nous croyons que l'apéritif est un moment sacré, celui où l'on se retrouve, 
              où l'on partage, où l'on découvre. C'est pourquoi nous mettons tout notre savoir-faire 
              dans chaque plateau, préparé avec amour et des ingrédients de qualité.
            </p>
            <p className="font-secondary text-cream/80 leading-relaxed">
              Basés à Bordeaux, nous intervenons pour tous vos événements : anniversaires, 
              mariages, soirées d'entreprise ou simplement un dimanche entre amis.
            </p>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800"
              alt="Plateaux apéritifs"
              className="w-full aspect-[4/5] object-cover"
            />
            <div className="absolute -bottom-6 -left-6 bg-gold p-6 md:p-8">
              <p className="font-script text-3xl text-black">Fait avec passion</p>
            </div>
          </div>
        </div>

        {/* Services */}
        <div className="py-16 border-t border-gold/20">
          <h2 className="font-primary text-3xl text-cream text-center mb-12">Nos Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Star size={32} />,
                title: "Livraison",
                desc: "Livraison à domicile ou sur le lieu de votre événement à Bordeaux et alentours."
              },
              {
                icon: <Users size={32} />,
                title: "Dressage",
                desc: "Mise en place et présentation soignée pour un effet visuel garanti."
              },
              {
                icon: <Sparkles size={32} />,
                title: "Assistance Traiteur",
                desc: "Service complet pour vos réceptions : de la préparation au service."
              }
            ].map((value, idx) => (
              <div key={idx} data-testid={`service-card-${idx}`} className="text-center p-8 border border-gold/20">
                <div className="text-gold mb-4 flex justify-center">{value.icon}</div>
                <h3 className="font-primary text-xl text-cream mb-3">{value.title}</h3>
                <p className="font-secondary text-cream/70 text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div className="py-16 border-t border-gold/20 text-center">
          <h2 className="font-primary text-3xl text-cream mb-8">Contactez-nous</h2>
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            <div className="flex items-center gap-3 text-cream/80">
              <Phone size={24} className="text-gold" />
              <span className="font-secondary text-lg">06 49 30 13 75</span>
            </div>
            <div className="flex items-center gap-3 text-cream/80">
              <Mail size={24} className="text-gold" />
              <span className="font-secondary text-lg">inivacristal30@gmail.com</span>
            </div>
            <div className="flex items-center gap-3 text-cream/80">
              <MapPin size={24} className="text-gold" />
              <span className="font-secondary text-lg">Bordeaux et alentours</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pt-12">
          <Link
            to="/commander"
            data-testid="about-cta-btn"
            className="inline-block border border-gold bg-gold text-black px-10 py-4 font-secondary text-sm uppercase tracking-widest hover:bg-gold-light transition-all duration-300"
          >
            Passons à Table
          </Link>
        </div>
      </section>
    </div>
  );
};

// Order Page
const OrderPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: '',
    event_date: '',
    guest_count: '',
    platter_preferences: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/quote-request`, {
        ...formData,
        guest_count: parseInt(formData.guest_count) || 0
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting quote request:", error);
      alert("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="order-page" className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <div className="text-center px-6">
          <div className="w-20 h-20 border-2 border-gold rounded-full flex items-center justify-center mx-auto mb-8">
            <Check size={40} className="text-gold" />
          </div>
          <h2 className="font-primary text-3xl text-cream mb-4">Demande Envoyée !</h2>
          <p className="font-secondary text-cream/70 mb-8">
            Merci pour votre demande. Nous vous recontacterons très rapidement.
          </p>
          <Link
            to="/"
            data-testid="back-home-btn"
            className="inline-block border border-gold text-gold px-8 py-3 font-secondary text-sm uppercase tracking-widest hover:bg-gold hover:text-black transition-all duration-300"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="order-page" className="min-h-screen bg-black pt-24">
      {/* Header */}
      <section className="py-16 md:py-24 text-center">
        <p className="font-secondary text-gold text-sm uppercase tracking-[0.2em] mb-4">Commander</p>
        <h1 className="font-primary text-4xl md:text-6xl text-cream mb-6">Demande de Devis</h1>
        <p className="font-secondary text-cream/70 max-w-2xl mx-auto px-6">
          Décrivez-nous votre événement et vos envies
        </p>
      </section>

      {/* Form */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-20">
        <form
          data-testid="quote-form"
          onSubmit={handleSubmit}
          className="space-y-6 p-8 md:p-12 border border-gold/30 bg-neutral-950"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-secondary text-cream/80 text-sm mb-2">Nom complet *</label>
              <input
                type="text"
                name="name"
                data-testid="input-name"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold"
                placeholder="Votre nom"
              />
            </div>
            <div>
              <label className="block font-secondary text-cream/80 text-sm mb-2">Email *</label>
              <input
                type="email"
                name="email"
                data-testid="input-email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold"
                placeholder="votre@email.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-secondary text-cream/80 text-sm mb-2">Téléphone *</label>
              <input
                type="tel"
                name="phone"
                data-testid="input-phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold"
                placeholder="06 XX XX XX XX"
              />
            </div>
            <div>
              <label className="block font-secondary text-cream/80 text-sm mb-2">Type d'événement *</label>
              <select
                name="event_type"
                data-testid="input-event-type"
                required
                value={formData.event_type}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold"
              >
                <option value="">Sélectionnez...</option>
                <option value="anniversaire">Anniversaire</option>
                <option value="mariage">Mariage / Fiançailles</option>
                <option value="entreprise">Événement d'entreprise</option>
                <option value="soiree">Soirée entre amis</option>
                <option value="vin_honneur">Vin d'honneur</option>
                <option value="brunch">Brunch</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block font-secondary text-cream/80 text-sm mb-2">Date souhaitée *</label>
              <input
                type="date"
                name="event_date"
                data-testid="input-date"
                required
                value={formData.event_date}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold"
              />
            </div>
            <div>
              <label className="block font-secondary text-cream/80 text-sm mb-2">Nombre d'invités *</label>
              <input
                type="number"
                name="guest_count"
                data-testid="input-guests"
                required
                min="2"
                max="100"
                value={formData.guest_count}
                onChange={handleChange}
                className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold"
                placeholder="Ex: 10"
              />
            </div>
          </div>

          <div>
            <label className="block font-secondary text-cream/80 text-sm mb-2">Formule souhaitée *</label>
            <select
              name="platter_preferences"
              data-testid="input-preferences"
              required
              value={formData.platter_preferences}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold"
            >
              <option value="">Sélectionnez...</option>
              <option value="mignardises">Buffet Mignardises (18€/pers)</option>
              <option value="mignardises_cocktail">Buffet + Cocktail (23€/pers)</option>
              <option value="personnalise">Formule personnalisée (sur devis)</option>
            </select>
          </div>

          <div>
            <label className="block font-secondary text-cream/80 text-sm mb-2">Message (optionnel)</label>
            <textarea
              name="message"
              data-testid="input-message"
              rows="4"
              value={formData.message}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 text-cream p-4 font-secondary focus:border-gold focus:ring-1 focus:ring-gold resize-none"
              placeholder="Allergies, préférences particulières, choix de cocktails..."
            />
          </div>

          <button
            type="submit"
            data-testid="submit-quote-btn"
            disabled={isSubmitting}
            className="w-full border border-gold bg-gold text-black py-4 font-secondary text-sm uppercase tracking-widest hover:bg-gold-light transition-all duration-300 disabled:opacity-50"
          >
            {isSubmitting ? 'Envoi en cours...' : 'Envoyer ma demande'}
          </button>
        </form>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="font-secondary text-cream/60 text-sm mb-4">
            Vous préférez nous contacter directement ?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-cream/80">
              <Phone size={18} className="text-gold" />
              <span className="font-secondary text-sm">06 49 30 13 75</span>
            </div>
            <div className="flex items-center gap-2 text-cream/80">
              <Mail size={18} className="text-gold" />
              <span className="font-secondary text-sm">inivacristal30@gmail.com</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-black min-h-screen">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/cocktails" element={<CocktailsPage />} />
          <Route path="/galerie" element={<GalleryPage />} />
          <Route path="/a-propos" element={<AboutPage />} />
          <Route path="/commander" element={<OrderPage />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
